<template>
  <v-card :disabled="loading" flat>
    <!-- actions top -->
    <v-card-text class="d-flex flex-wrap align-center justify-space-between pb-2">
      <div class="d-flex align-center">
        <v-btn
          :disabled="!hasMissingDefaults"
          @click="addDefaults"
          outlined
          color="primary"
          class="ml-2"
        >
          <v-icon left>mdi-account-multiple-plus</v-icon>
          افزودن فیلدهای پیش‌فرض حضور و غیاب
        </v-btn>

        <v-btn @click="deleted('all')" outlined color="error" class="ml-2">
          <v-icon left>mdi-delete-sweep</v-icon>
          {{ $t("delete_all") || 'حذف همه' }}
        </v-btn>
      </div>
    </v-card-text>

    <!-- listed items -->
    <v-list class="pa-2">
      <v-row dense>
        <v-col
          v-for="(item, indexOf) in items"
          :key="item.id || indexOf"
          cols="12"
          class="pa-2"
        >
          <v-list-group
            v-model="item.active"
            :prepend-icon="item.action"
            no-action
            :ripple="false"
            class="border pa-2 elevation-0 rounded-lg"
            style="border: 1px solid #e2e8f0 !important;"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <div class="d-flex align-center justify-space-between w-100">
                  <span class="font-weight-bold">{{ item.items[0].label || item.title }}</span>
                  
                  <!-- دکمه‌های جابجایی ترتیب -->
                  <div class="d-flex align-center" @click.stop>
                    <v-btn
                      icon
                      small
                      :disabled="indexOf === 0"
                      @click="moveItem(indexOf, -1)"
                      title="انتقال به بالا"
                    >
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="indexOf === items.length - 1"
                      @click="moveItem(indexOf, 1)"
                      title="انتقال به پایین"
                    >
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-list-item-content>
            </template>

            <!-- تنظیمات داخلی فیلد (ویرایش کامل) -->
            <v-list-item
              v-for="(child, indexChild) in item.items"
              :key="indexChild"
              class="pa-2 d-flex flex-column align-stretch"
            >
              <div class="w-100">
                <v-row dense>
                  <v-col cols="12" class="d-flex justify-end pb-0">
                    <v-btn
                      @click="deleted(item.id)"
                      outlined
                      small
                      color="error"
                    >
                      <v-icon small left>mdi-delete</v-icon>
                      حذف این فیلد
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row dense class="mt-2 align-center bg-light rounded-lg pa-2">
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <span class="ml-2 font-weight-medium">وضعیت فیلد:</span>
                    <v-switch
                      v-model="child.action"
                      hide-details
                      dense
                      class="ma-0 pa-0"
                      :label="child.action ? 'فعال در فرم' : 'غیرفعال'"
                      @change="syncToParent"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-radio-group v-model="child.optional" row mandatory dense hide-details @change="syncToParent">
                      <v-radio label="اختیاری" :value="false" />
                      <v-radio label="الزامی (اجباری)" :value="true" />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-divider class="my-3"></v-divider>

                <!-- ویرایش مشخصات اصلی فیلد -->
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="child.title"
                      label="نام فیلد (شناسه سیستم)"
                      outlined
                      dense
                      hide-details
                      @input="item.title = child.title; syncToParent()"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="child.label"
                      label="برچسب فیلد (نمایش به کاربر)"
                      outlined
                      dense
                      hide-details
                      @input="syncToParent"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="child.placeholder"
                      label="متن راهنما (Placeholder)"
                      placeholder="مثلاً: اینجا بنویسید..."
                      outlined
                      dense
                      hide-details
                      @input="syncToParent"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                     <icon-picker v-model="item.action" @input="syncToParent" />
                  </v-col>
                </v-row>

                <!-- تنظیمات مخصوص نوع انتخابی (Dropdown) -->
                <v-row dense v-if="child.type === 'select'" class="mt-2">
                  <v-col cols="12">
                    <v-combobox
                      v-model="child.selectItems"
                      chips
                      dense
                      multiple
                      outlined
                      label="گزینه‌های لیست (پس از تایپ اینتر بزنید)"
                      hint="برای افزودن هر گزینه Enter بزنید"
                      persistent-hint
                      @change="syncToParent"
                    >
                      <template v-slot:selection="{ attrs, item: opt, select, selected }">
                        <v-chip
                          :input-value="selected"
                          close
                          small
                          v-bind="attrs"
                          @click="select"
                          @click:close="removeData(opt, indexOf, indexChild)"
                        >
                          {{ opt }}
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>

                <!-- تنظیمات پیشرفته و الگو (REGEX) -->
                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-btn
                      text
                      small
                      color="primary"
                      @click="$set(item, 'showSettings', !item.showSettings)"
                      class="px-0"
                    >
                      <v-icon left small>mdi-cog-outline</v-icon>
                      تنظیمات پیشرفته و الگو (REGEX)
                      <v-icon right small>{{ item.showSettings ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-expand-transition>
                  <div v-if="item.showSettings">
                    <v-row dense class="mt-1">
                      <v-col cols="12" md="6">
                        <!-- پشتیبانی از انتخاب چندگانه (Multiple) قوانین -->
                        <v-select
                          v-model="child.setting.regex"
                          dense
                          outlined
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          label="الگوی ورودی مجاز (می‌توانید چند مورد انتخاب کنید)"
                          :items="regexList"
                          item-value="value"
                          item-text="text"
                          @change="syncToParent"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="child.setting.max"
                          type="number"
                          label="حداکثر کاراکتر"
                          outlined
                          dense
                          @input="syncToParent"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="child.setting.min"
                          type="number"
                          label="حداقل کاراکتر"
                          outlined
                          dense
                          @input="syncToParent"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </div>
            </v-list-item>
          </v-list-group>
        </v-col>
      </v-row>
    </v-list>

    <!-- همیشه دکمه افزودن فیلد جدید بدون نیاز به ذخیره دستی -->
    <v-card-actions class="pa-4">
      <v-btn
        @click="dialog = true"
        block
        class="btn-get-code rounded-lg"
      >
        <v-icon left>mdi-plus-circle-outline</v-icon>
        افزودن فیلد سفارشی جدید
      </v-btn>
    </v-card-actions>

    <!-- modal افزودن فیلد جدید -->
    <custom-popup
      @action="addNewItem"
      max-width="600px"
      v-model="dialog"
      :cons="{ title: 'افزودن فیلد جدید به فرم ثبت‌نام پرسنل' }"
    >
      <v-card class="pa-3" flat>
        <v-row dense>
          <v-col cols="12" md="6">
            <icon-picker v-model="newItem.action" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="newItem.items[0].type"
              dense
              outlined
              label="نوع ورودی"
              :items="[
                { value: 'text', text: 'متن کوتاه' },
                { value: 'number', text: 'عدد (شماره پرسنلی/کارت)' },
                { value: 'select', text: 'لیست انتخابی (دراپ‌داون)' },
                { value: 'textarea', text: 'متن چند خطی (توضیحات)' },
                { value: 'date', text: 'تاریخ' },
                { value: 'time', text: 'زمان (ساعت)' },
              ]"
              item-value="value"
              item-text="text"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              :rules="[(v) => !!v || 'شناسه فیلد الزامی است']"
              label="نام فیلد (شناسه)"
              placeholder="مثال: code_nfc"
              outlined
              dense
              v-model="newItem.title"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              label="برچسب (نمایش به کاربر)"
              placeholder="مثال: کد کارت"
              outlined
              dense
              v-model="newItem.items[0].label"
            />
          </v-col>

           <v-col cols="12" md="4">
            <v-text-field
              label="متن راهنما (Placeholder)"
              placeholder="مثال: تگ را اسکن کنید"
              outlined
              dense
              v-model="newItem.items[0].placeholder"
            />
          </v-col>

          <v-col cols="12">
            <v-btn
              text
              small
              color="grey darken-1"
              @click="setting = !setting"
              class="px-0"
            >
              <v-icon left small>mdi-cog-outline</v-icon>
              تنظیمات پیشرفته و الگو (Regex)
              <v-icon right small>{{ setting ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </v-col>

          <v-expand-transition>
            <div v-if="setting" class="w-100 mt-2">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newItem.items[0].setting.regex"
                    dense
                    outlined
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    label="الگوی ورودی مجاز"
                    :items="regexList"
                    item-value="value"
                    item-text="text"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newItem.items[0].setting.max"
                    type="number"
                    label="حداکثر کاراکتر"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newItem.items[0].setting.min"
                    type="number"
                    label="حداقل کاراکتر"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-row>
      </v-card>
    </custom-popup>
  </v-card>
</template>

<script>
import IconPicker from "@/view/components/IconPicker.vue";
import { mapActions, mapState } from "vuex";
import _ from "lodash";

export default {
  name: "AttendanceRegistrationFields",
  components: { IconPicker },
  props: {
    value: Object,
    info: Object,
  },
  data() {
    return {
      defaultItems: [
        {
          id: 3001,
          title: "شماره پرسنلی",
          action: "mdi-badge-account-horizontal",
          active: false,
          showSettings: false,
          items: [
            {
              title: "شماره پرسنلی",
              label: "شماره پرسنلی",
              placeholder: "مثال: 1042",
              optional: true,
              action: true,
              type: "number",
              setting: {
                regex: ["number"], // تبدیل به آرایه
                min: null,
                max: null,
              },
            },
          ],
        },
        {
          id: 3002,
          title: "نام",
          action: "mdi-account",
          active: false,
          showSettings: false,
          items: [
            {
              title: "نام",
              label: "نام",
              placeholder: "مثال: علی",
              optional: true,
              action: true,
              type: "text",
              setting: {
                regex: ["persian"],
                min: null,
                max: null,
              },
            },
          ],
        },
        {
          id: 3003,
          title: "نام خانوادگی",
          action: "mdi-account-tie",
          active: false,
          showSettings: false,
          items: [
            {
              title: "نام خانوادگی",
              label: "نام خانوادگی",
              placeholder: "مثال: محمدی",
              optional: true,
              action: true,
              type: "text",
              setting: {
                regex: ["persian"],
                min: null,
                max: null,
              },
            },
          ],
        },
        {
          id: 3004,
          title: "شماره موبایل",
          action: "mdi-cellphone",
          active: false,
          showSettings: false,
          items: [
            {
              title: "شماره موبایل",
              label: "شماره موبایل",
              placeholder: "09xxxxxxxxx",
              optional: true,
              action: true,
              type: "text",
              setting: {
                regex: ["mobile", "number"],
                min: 11,
                max: 11,
              },
            },
          ],
        },
        {
          id: 3005,
          title: "کد ملی",
          action: "mdi-card-account-details-outline",
          active: false,
          showSettings: false,
          items: [
            {
              title: "کد ملی",
              label: "کد ملی",
              placeholder: "۱۰ رقم بدون خط تیره",
              optional: true,
              action: true,
              type: "text",
              setting: {
                regex: ["meliCode"],
                min: 10,
                max: 10,
              },
            },
          ],
        },
        {
          id: 3006,
          title: "واحد / دپارتمان",
          action: "mdi-domain",
          active: false,
          showSettings: false,
          items: [
            {
              title: "واحد / دپارتمان",
              label: "واحد سازمانی",
              placeholder: "یک مورد را انتخاب کنید",
              optional: false,
              action: true,
              type: "select",
              selectItems: ["اداری و مالی", "فنی و مهندسی", "فروش و بازاریابی", "پشتیبانی"],
              setting: { regex: [], min: null, max: null },
            },
          ],
        },
        {
          id: 3007,
          title: "سمت شغلی",
          action: "mdi-briefcase-outline",
          active: false,
          showSettings: false,
          items: [
            {
              title: "سمت شغلی",
              label: "سمت شغلی",
              placeholder: "مثال: مدیر فنی",
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: ["persian"],
                min: null,
                max: null,
              },
            },
          ],
        },
      ],

      items: [],
      newItem: this.getEmptyNewItem(),

      dialog: false,
      loading: false,
      setting: false,

      regexList: [
        { text: "فقط عدد", value: "number" },
        { text: "فقط حروف فارسی", value: "persian" },
        { text: "حروف انگلیسی", value: "english" },
        { text: "ترکیب فارسی و انگلیسی", value: "mixPerEng" },
        { text: "شماره موبایل", value: "mobile" },
        { text: "کد ملی", value: "meliCode" },
        { text: "ایمیل", value: "email" },
      ],
    };
  },

  computed: {
    ...mapState("local", ["qr_id", "qrInfo"]),
    hasMissingDefaults() {
      const existingTitles = new Set(
        Array.isArray(this.items) ? this.items.map((item) => item.title || item.items[0].title) : []
      );
      return this.defaultItems.some((d) => !existingTitles.has(d.title));
    },
  },

  mounted() {
    this.initFromValue();
  },

  methods: {
    ...mapActions("local", ["updateQrInfoChange"]),

    getEmptyNewItem() {
      return {
        active: false,
        action: "mdi-form-textbox",
        showSettings: false,
        title: "",
        items: [
          {
            title: "",
            label: "",
            placeholder: "",
            optional: false,
            action: true,
            type: "text",
            selectItems: [],
            setting: { regex: [], min: null, max: null },
          },
        ],
      };
    },

    moveItem(index, direction) {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= this.items.length) return;

      const itemToMove = this.items.splice(index, 1)[0];
      this.items.splice(targetIndex, 0, itemToMove);
      this.syncToParent();
    },

    removeData(opt, indexOf, indexChild) {
      this.items[indexOf].items[indexChild].selectItems = this.items[
        indexOf
      ].items[indexChild].selectItems.filter((item) => item !== opt);
      this.syncToParent();
    },

    addNewItem(e) {
      if (e === "submit") {
        let newItems = [...this.items];
        
        // نرمالایز کردن type
        if (typeof this.newItem.items[0].type === "object" && this.newItem.items[0].type?.value) {
          this.newItem.items[0].type = this.newItem.items[0].type.value;
        }

        this.newItem.id = Date.now();
        this.newItem.items[0].title = this.newItem.title;
        
        newItems.push(_.cloneDeep(this.newItem));

        this.items = newItems;
        this.syncToParent();
        this.dialog = false;
        this.newItem = this.getEmptyNewItem();
        this.setting = false;
        this.$toast.success("فیلد جدید با موفقیت افزوده شد");
      }
    },

    addDefaults() {
      let currentItems = this.items ? [...this.items] : [];
      const existingTitles = new Set(currentItems.map((item) => item.title || item.items[0].title));

      const itemsToAdd = this.defaultItems.filter((defaultItem) => {
        return !existingTitles.has(defaultItem.title);
      });

      this.items = [...currentItems, ..._.cloneDeep(itemsToAdd)];
      this.syncToParent();
      this.$toast.success("فیلدهای پیش‌فرض اضافه شدند");
    },

    deleted(id) {
      if (id === "all") {
        this.items = [];
      } else {
        this.items = this.items.filter((item) => item.id !== id);
      }
      this.syncToParent();
      this.$toast.success(id === "all" ? "تمام فیلدها پاک شدند" : "فیلد حذف شد");
    },

    syncToParent() {
      if (!this.value) return;
      this.$set(this.value, "items", this.items);
      // حذف کامل conform از پراپ خروجی
      this.$delete(this.value, "conform");
      this.$emit("input", this.value);
      if (typeof this.updateQrInfoChange === 'function') {
        this.updateQrInfoChange();
      }
    },

    initFromValue() {
      if (this.value && Array.isArray(this.value.items) && this.value.items.length > 0) {
        let loadedItems = _.cloneDeep(this.value.items);
        
        // میگریشن دیتاهای قدیمی برای ساپورت Multiple Select روی Regex
        loadedItems.forEach((item) => {
          this.$set(item, 'showSettings', false);
          if (item.items && item.items[0] && item.items[0].setting) {
             let currentRegex = item.items[0].setting.regex;
             if (!Array.isArray(currentRegex)) {
                if (currentRegex && currentRegex.value) {
                  item.items[0].setting.regex = [currentRegex.value];
                } else if (typeof currentRegex === 'string') {
                  item.items[0].setting.regex = [currentRegex];
                } else {
                  item.items[0].setting.regex = [];
                }
             }
          }
        });
        
        this.items = loadedItems;
      } else {
        this.items = _.cloneDeep(this.defaultItems);
        this.syncToParent();
      }
    },
  },

  watch: {
    value: {
      handler(val) {
        if (val && Array.isArray(val.items) && !_.isEqual(val.items, this.items)) {
          // فقط وقتی از بیرون پراپ جدید میاد سینک کن (جلوگیری از لوپ)
          this.initFromValue();
        }
      },
      deep: true,
    }
  },
};
</script>

<style scoped>
.bg-light {
  background-color: #f8fafc;
}
.w-100 {
  width: 100%;
}
</style>
